import { apiCall } from '@/lib/api-client';
import type { ExtractionProposal } from './types';

export async function getExtractionProposal(
  productId: number,
): Promise<ExtractionProposal> {
  return apiCall<ExtractionProposal>(
    `/api/products/${productId}/extraction-proposal`,
    { method: 'GET' },
  );
}

/** One inline correction to a flagged section — its document `order` + the edited markdown. */
export interface SectionEdit {
  order: number;
  markdown: string;
}

/** Approve via the SECTION review — the kept sections (by document `order`) become the
 *  product's structured spec. `edits` carries inline corrections to flagged sections. */
export async function approveSections(
  productId: number,
  keptOrders: number[],
  edits: SectionEdit[] = [],
): Promise<{ ok: boolean; section_count: number }> {
  return apiCall<{ ok: boolean; section_count: number }>(
    `/api/products/${productId}/extraction-proposal/approve-sections`,
    { method: 'POST', json: { kept_orders: keptOrders, edits } },
  );
}

/** Save one in-progress section edit (on Done / click-away) so it survives leaving the
 *  review before approving. Approve still decides what reaches the product's spec. */
export async function saveSectionEdit(
  productId: number,
  edit: SectionEdit,
): Promise<{ ok: boolean }> {
  return apiCall<{ ok: boolean }>(
    `/api/products/${productId}/extraction-proposal/section-edit`,
    { method: 'POST', json: edit },
  );
}

/** What kind of change a spec save represents — recorded in the audit trail (all of these
 *  funnel through the one PUT, so the server can't otherwise tell them apart). */
export type SectionChangeKind = 'edit' | 'delete' | 'reorder' | 'add';

/** Save an edited section-approved product spec. Writes Product.tech_spec_markdown; the
 *  proposal's source_markdown stays the immutable original. `ifMatch` is the product's last
 *  `updated_at` (optimistic concurrency — a stale value throws ApiConflictError/409); the
 *  response carries the new `updated_at` to chain the next save. */
export async function saveTechSpecMarkdown(
  productId: number,
  markdown: string,
  changeKind: SectionChangeKind,
  ifMatch: string,
): Promise<{ ok: boolean; updated_at: string }> {
  return apiCall<{ ok: boolean; updated_at: string }>(
    `/api/products/${productId}/tech-spec-markdown`,
    { method: 'PUT', json: { markdown, change_kind: changeKind }, headers: { 'If-Match': ifMatch } },
  );
}

/** URL of the original uploaded document — shown alongside the section review. */
export function extractionSourceUrl(productId: number): string {
  return `/api/products/${productId}/extraction-proposal/source`;
}
